/* WalkingTracker — ORIO's daily-driver feature. Apple-Watch style live
   tracker with 3 concentric progress rings (km · time · kcal), a live map
   showing your trail + hazards nearby + online peers (Live Presence),
   and a per-dog inline history with tap-to-expand walk details.

   Screens (all under `/app/passeggiate`):
     • Start panel (dog picker + per-dog history)  → default view
     • Live tracker (3 rings + live map overlay)   → when a walk is active
     • Summary                                     → shown right after end()
   Backend endpoints in server.py: /api/walks/*  (all PRO)
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import {
  ArrowLeft, PawPrint, Play, Square, Timer, Flame, Zap, TrendingUp,
  Trophy, MapPin, Loader2, ChevronDown, Sparkles, Crown, AlertTriangle, Users,
  Bell, Check, Compass, Wind, Sun, Droplet, ChevronRight, Route, X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap, hapticSuccess, hapticWarning } from "@/lib/haptics";
import { orioIllustration } from "@/lib/illustrations";
import BadgeUnlockAnimation from "@/components/BadgeUnlockAnimation";

// Warm ORIO accent palette.
const C = {
  cream:  "#FBF7F1",
  ivory:  "#F4EFE6",
  border: "#F0EBE3",
  fg:     "#0F0F0F",
  muted:  "#6B6B6B",
  orange: "#FF7A1A",
  amber:  "#F4B83C",
  green:  "#34D399",
  red:    "#E94545",
  blue:   "#5B9EE0",
  ringBg: "#EFE7DA",
};

// Daily goals — realistic for a dog walk.
const GOAL_KM = 3.0;
const GOAL_MIN = 30;
const GOAL_KCAL = 100;

// Great-circle distance in meters (small-angle approx — accurate < 5km).
// Shared across LiveTracker, hazard proximity, etc.
function distanceM(a, b) {
  const dLat = (b.lat - a.lat) * 111000;
  const dLng = (b.lng - a.lng) * 111000 * Math.cos(a.lat * Math.PI / 180);
  return Math.hypot(dLat, dLng);
}

/* ---------- Icons ---------- */
const dogIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:9999px;background:#FF7A1A;
    box-shadow:0 0 0 6px rgba(255,122,26,0.20),0 0 0 12px rgba(255,122,26,0.10);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function makeHazardIcon(color = "#E94545", symbol = "!") {
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:32px;height:32px;display:flex;align-items:flex-end;justify-content:center;">
      <span style="position:absolute;left:50%;bottom:2px;width:24px;height:24px;margin-left:-12px;border-radius:9999px;background:${color};opacity:0.35;animation:orio-ping 1.8s cubic-bezier(0,0,0.2,1) infinite"></span>
      <div style="position:relative;width:22px;height:22px;display:flex;align-items:center;justify-content:center;
                  background:${color}; border:2px solid #FFFFFF;
                  clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
                  color:white; font-size:11px; font-weight:bold;
                  filter: drop-shadow(0 2px 6px ${color}80);">
        <span style="position:relative;top:4px;">${symbol}</span>
      </div>
    </div>`,
    iconSize: [32, 32], iconAnchor: [16, 28],
  });
}

function makePeerIcon(photoUrl, name) {
  const initials = (name || "?").split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
  const inner = photoUrl
    ? `<img src="${photoUrl}" alt="" style="width:100%;height:100%;object-fit:cover"/>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#34D399,#2DBA60);color:#fff;font-size:10px;font-weight:700">${initials}</div>`;
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:34px;height:34px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:-2px;border-radius:9999px;background:#2DBA60;opacity:0.30;animation:orio-ping 2.6s cubic-bezier(0,0,0.2,1) infinite"></span>
      <div style="position:relative;width:28px;height:28px;border-radius:9999px;border:2px solid #fff;background:#fff;overflow:hidden;
                  box-shadow:0 0 0 2px #2DBA60, 0 4px 10px -2px rgba(45,186,96,0.5);">
        ${inner}
      </div>
    </div>`,
    iconSize: [34, 34], iconAnchor: [17, 17],
  });
}

/* ---------- Format helpers ---------- */
const fmtHMS = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    : `${m}:${String(ss).padStart(2, "0")}`;
};
const fmtPace = (minPerKm) => {
  if (!minPerKm || minPerKm < 0.1) return "—";
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  return `${m}'${String(s).padStart(2, "0")}"`;
};

/* Animated counter: interpolates between value updates with a spring. */
function AnimatedNumber({ value, format = (v) => v.toFixed(0), className, style }) {
  const mv = useMotionValue(value);
  const spring = useSpring(mv, { stiffness: 90, damping: 20, mass: 0.8 });
  const [display, setDisplay] = useState(format(value));
  useEffect(() => { mv.set(value); }, [value, mv]);
  useEffect(() => {
    const unsub = spring.on("change", (v) => setDisplay(format(v)));
    return () => unsub();
  }, [spring, format]);
  return <span className={className} style={style}>{display}</span>;
}

/* ============================================================
   ORIO Rings — 3 concentric progress arcs like Apple Watch.
   Uses SVG stroke-dashoffset for smooth animation.
   ============================================================ */
function OrioRings({ km, minutes, kcal, size = 240, dark = false }) {
  const strokes = 14;              // arc thickness
  const gap = 6;                   // gap between rings
  const rings = dark
    ? [
        // Nike Training-style bright neon on jet-black tracks.
        { color: "#FF7A1A", bg: "rgba(255,122,26,0.14)", value: km,      goal: GOAL_KM,   label: "km" },
        { color: "#F4B83C", bg: "rgba(244,184,60,0.14)", value: minutes, goal: GOAL_MIN,  label: "min" },
        { color: "#34D399", bg: "rgba(52,211,153,0.14)", value: kcal,    goal: GOAL_KCAL, label: "kcal" },
      ]
    : [
        { color: C.orange, bg: "#FFEAD6", value: km,      goal: GOAL_KM,   label: "km" },
        { color: C.green,  bg: "#DEF7E9", value: minutes, goal: GOAL_MIN,  label: "min" },
        { color: C.blue,   bg: "#DDECFA", value: kcal,    goal: GOAL_KCAL, label: "kcal" },
      ];
  const cx = size / 2;
  const cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
         className="drop-shadow-sm" data-testid="walk-rings">
      {rings.map((r, i) => {
        const radius = (size / 2) - strokes / 2 - (strokes + gap) * i;
        const circ = 2 * Math.PI * radius;
        const pct = Math.min(1, (r.value || 0) / r.goal);
        const dash = circ * pct;
        return (
          <g key={i} transform={`rotate(-90 ${cx} ${cy})`}>
            {/* Background track */}
            <circle cx={cx} cy={cy} r={radius}
                    fill="none" stroke={r.bg} strokeWidth={strokes} />
            {/* Progress arc */}
            <motion.circle
              cx={cx} cy={cy} r={radius}
              fill="none" stroke={r.color} strokeWidth={strokes}
              strokeLinecap="round"
              strokeDasharray={circ}
              initial={{ strokeDashoffset: circ }}
              animate={{ strokeDashoffset: circ - dash }}
              transition={{ type: "spring", stiffness: 60, damping: 18 }}
              style={{ filter: `drop-shadow(0 2px 8px ${r.color}66)` }}
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- Walk Coach Modal ----------
   Opens on tap of the big "Cammina con…" CTA. Shows safety/coaching
   instructions AND runs a GPS warmup — we wait until 3 consecutive
   readings arrive with accuracy ≤ 15m so the walk never starts with a
   bad "jump" fix (the thing the user explicitly asked to fix). */
function WalkCoachModal({ dogs, onClose, onConfirm, starting }) {
  const [acc, setAcc] = useState(null);      // last GPS accuracy in meters
  const [locked, setLocked] = useState(0);   // # consecutive good fixes
  const [ready, setReady] = useState(false); // 3 consecutive good fixes
  const [forceEnabled, setForceEnabled] = useState(false); // after 8s or 1 excellent

  useEffect(() => {
    if (!navigator.geolocation) return;
    let goodStreak = 0;
    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        const a = p.coords.accuracy;
        setAcc(a);
        // Single excellent reading (≤ 8m) is enough to allow starting
        if (a != null && a <= 8) setForceEnabled(true);
        if (a <= 15) {
          goodStreak += 1;
          setLocked(goodStreak);
          if (goodStreak >= 3) setReady(true);
        } else {
          goodStreak = 0;
          setLocked(0);
        }
      },
      () => { /* silent — modal still lets user force-start */ },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
    // Hard fallback: after 8s let the user start manually even if we
    // haven't locked (useful when the browser throttles watchPosition).
    const t = setTimeout(() => setForceEnabled(true), 8000);
    return () => { navigator.geolocation.clearWatch(watchId); clearTimeout(t); };
  }, []);

  // Visual signal: red → yellow → green based on accuracy
  const sig = acc == null
    ? { color: "#6B6B6B", label: "Attendo GPS…", pct: 10 }
    : acc > 30
      ? { color: "#E94545", label: `Segnale debole (${Math.round(acc)}m)`, pct: 25 }
      : acc > 15
        ? { color: "#F4B83C", label: `Buono (${Math.round(acc)}m)`, pct: 55 }
        : acc > 8
          ? { color: "#34D399", label: `Ottimo (${Math.round(acc)}m)`, pct: 85 }
          : { color: "#34D399", label: `Perfetto (${Math.round(acc)}m)`, pct: 100 };

  const dogNames = dogs.map((d) => d.name).join(" + ") || "il tuo cane";

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-3"
         data-testid="walk-coach-modal" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(20px)" }} />
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-[28px] sm:rounded-[28px] overflow-hidden border text-white"
        style={{
          background:
            "radial-gradient(100% 70% at 50% 0%, rgba(255,122,26,0.22) 0%, rgba(255,122,26,0) 65%), " +
            "linear-gradient(180deg, #1A1512 0%, #0B0806 100%)",
          borderColor: "rgba(255,255,255,0.12)",
        }}>
        <div className="p-6 sm:p-7">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)", color: "#0F0F0F" }}>
            <Sparkles className="h-3 w-3" /> ORIO Walk · Precisione
          </div>
          <h2 className="font-display text-[26px] tracking-tight mt-3 leading-[1.08]">
            Pronto a camminare<br />con {dogNames}?
          </h2>

          {/* GPS signal strength bar */}
          <div className="mt-5 rounded-2xl border p-4"
               style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-widest font-bold" style={{ color: sig.color }}>
                <Compass className="h-3.5 w-3.5" /> Segnale GPS
              </div>
              <div className="text-[11.5px] font-semibold" style={{ color: sig.color }}>{sig.label}</div>
            </div>
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${sig.pct}%` }}
                transition={{ duration: 0.4 }}
                className="h-full rounded-full"
                style={{ background: sig.color, boxShadow: `0 0 10px ${sig.color}80` }}
              />
            </div>
            <div className="mt-2 text-[11px] text-white/55 leading-snug">
              {ready
                ? "✅ Posizione agganciata con precisione. Puoi partire — nessun salto iniziale."
                : `In attesa di ${3 - locked} rilevazion${(3 - locked) === 1 ? "e" : "i"} precis${(3 - locked) === 1 ? "a" : "e"} prima di iniziare.`}
            </div>
          </div>

          {/* Coaching tips */}
          <div className="mt-5 space-y-2.5">
            {[
              { icon: "📱", title: "Blocca lo schermo", desc: "Metti il telefono in tasca o nella fascia da braccio — ORIO continua a tracciare." },
              { icon: "🚶", title: "Non uscire dall'app", desc: "Chiudere ORIO interrompe il GPS. Riaprila per vedere i dati live." },
              { icon: "🚗", title: "Niente auto o bici", desc: "Il tracker si pausa automaticamente se la velocità supera 20 km/h — non è una corsa." },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-3 rounded-2xl p-3 border"
                   style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}>
                <span className="text-xl shrink-0">{t.icon}</span>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-white leading-tight">{t.title}</div>
                  <div className="text-[11.5px] text-white/60 leading-snug mt-0.5">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onConfirm}
            disabled={(!ready && !forceEnabled) || starting}
            data-testid="walk-coach-confirm"
            className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              background: (ready || forceEnabled)
                ? "linear-gradient(135deg, #FF7A1A 0%, #E9542A 100%)"
                : "rgba(255,255,255,0.10)",
              boxShadow: (ready || forceEnabled) ? "0 22px 40px -14px rgba(233,84,42,0.75)" : "none",
            }}>
            {starting
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Partenza…</>
              : ready
                ? <><Play className="h-4 w-4" fill="white" /> Inizia ora</>
                : forceEnabled
                  ? <><Play className="h-4 w-4" fill="white" /> Inizia comunque</>
                  : <><Loader2 className="h-4 w-4 animate-spin" /> Aggancio GPS in corso…</>
            }
          </button>
          <button onClick={onClose} className="w-full mt-2 py-2 text-[12px] text-white/55 hover:text-white/85">
            Annulla
          </button>
        </div>
      </motion.div>
    </div>
  );
}



/* ---------- Start Panel (dog picker + per-dog history) ---------- */
function StartPanel({ dogs, onStart, starting, stats }) {
  // Multi-dog select: Set of dog IDs. Preselects first dog on first mount.
  const [selectedSet, setSelectedSet] = useState(() => new Set(dogs[0] ? [dogs[0].id] : []));
  const [hazards, setHazards] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weeklyDays, setWeeklyDays] = useState({}); // { "YYYY-MM-DD": true }
  const [showCoach, setShowCoach] = useState(false);
  useEffect(() => {
    if (selectedSet.size === 0 && dogs[0]) {
      setSelectedSet(new Set([dogs[0].id]));
    }
  }, [dogs, selectedSet.size]);
  const selectedDogs = dogs.filter((d) => selectedSet.has(d.id));
  const toggleDog = (id) => {
    hapticTap();
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Don't allow empty set — if last one, keep it
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const { user } = useAuth();

  // Live context: hazards nearby + weather. Silently ignored on errors so
  // the page renders even without a fresh network.
  useEffect(() => {
    (async () => {
      // Fetch current geo from browser (best-effort, silent).
      let lat = null, lng = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject();
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 4000, maximumAge: 30000 });
        });
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch { /* silent — fallback below */ }
      try {
        const q = lat != null && lng != null ? `?lat=${lat}&lng=${lng}&radius_km=15` : "?radius_km=15";
        const { data } = await api.get(`/hazards${q}`);
        setHazards((data?.hazards || data || []).slice(0, 3));
      } catch { /* silent */ }
      if (lat != null && lng != null) {
        try {
          const { data } = await api.get(`/weather?lat=${lat}&lng=${lng}`);
          setWeather(data);
        } catch { /* silent */ }
      }
      // Build a { date: true } map for the last 7 days from history —
      // powers the weekly-goal check-marks.
      try {
        const { data } = await api.get("/walks?limit=30");
        const map = {};
        for (const w of data?.walks || data || []) {
          const dt = (w.started_at || "").slice(0, 10);
          if (dt) map[dt] = true;
        }
        setWeeklyDays(map);
      } catch { /* silent */ }
    })();
  }, []);

  const weeklyPills = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const iso = d.toISOString().slice(0, 10);
      arr.push({
        iso,
        label: d.toLocaleDateString("it-IT", { weekday: "short" }).slice(0, 3),
        day: d.getDate(),
        isToday: iso === now.toISOString().slice(0, 10),
        done: !!weeklyDays[iso],
      });
    }
    return arr;
  }, [weeklyDays]);

  const kmWeek = ((stats?.week?.distance_m || 0) / 1000).toFixed(1);
  const kcalWeek = (stats?.week?.dog_kcal || 0) + (stats?.week?.human_kcal || 0);
  const walksWeek = stats?.week?.count || 0;

  const firstName = (user?.name || "").split(" ")[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Buongiorno" : hour < 19 ? "Buon pomeriggio" : "Buonasera";

  return (
    <div
      className="min-h-[100dvh] px-4 pt-6 pb-[140px] max-w-md mx-auto text-white font-display-body"
      data-testid="walk-start-panel"
      style={{
        background:
          "radial-gradient(120% 60% at 20% -10%, rgba(255,122,26,0.24) 0%, rgba(255,122,26,0) 60%), " +
          "radial-gradient(120% 60% at 100% 0%, rgba(244,184,60,0.18) 0%, rgba(244,184,60,0) 55%), " +
          "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
      }}
    >
      <Link to="/app" className="text-[12px] text-white/50 hover:text-white inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Home
      </Link>

      {/* Header — greeting + avatar + PRO chip */}
      <div className="mt-4 flex items-start gap-3">
        <div className="h-14 w-14 rounded-2xl overflow-hidden bg-white/10 shrink-0" data-testid="walk-user-avatar">
          {user?.photo_url ? (
            <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/70 font-display text-lg">
              {(firstName || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">{greet}!</div>
          <div className="font-display text-2xl leading-tight text-white truncate" style={{ fontWeight: 700 }}>
            {firstName || "Amico"}
          </div>
        </div>
        {user?.is_pro && (
          <span
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10.5px] font-bold text-white"
            style={{
              background: "linear-gradient(135deg,#FF7A1A,#F4B83C)",
              boxShadow: "0 12px 24px -8px rgba(255,122,26,0.55)",
            }}
          >
            <Crown className="h-3 w-3" strokeWidth={2.5} /> PRO
          </span>
        )}
      </div>

      {/* Weather + safety chip strip (compact, cinema-dark) */}
      {(weather || hazards.length > 0) && (
        <div className="mt-4 flex gap-2 overflow-x-auto orio-h-scroll pb-1" data-testid="walk-safety-strip">
          {weather?.temp_c != null && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-white shrink-0"
                  style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.14)" }}>
              <Sun className="h-3.5 w-3.5" style={{ color: "#F4B83C" }} />
              {Math.round(weather.temp_c)}° {weather.hint || (weather.temp_c > 28 ? "Cerca ombra" : weather.temp_c < 5 ? "Copri il cane" : "Ottimo per uscire")}
            </span>
          )}
          {hazards.map((h, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-white shrink-0"
                  style={{ background: "rgba(233,84,42,0.18)", border: "1px solid rgba(233,84,42,0.35)" }}>
              <AlertTriangle className="h-3.5 w-3.5" style={{ color: "#FF9E45" }} />
              {(h.type || "pericolo")} · {h.distance_km != null ? `${h.distance_km.toFixed(1)}km` : "vicino"}
            </span>
          ))}
        </div>
      )}

      {/* Weekly goal — day pills matching Apple Fitness / mockup */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">Obiettivo settimanale</div>
          <div className="font-display text-[22px] leading-tight text-white mt-0.5" style={{ fontWeight: 700 }}>
            {walksWeek}/6 uscite
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl tabular-nums text-white" style={{ fontWeight: 700, color: "#FF7A1A" }}>
            {kmWeek}<span className="text-[13px] text-white/60 ml-1">km</span>
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.20em] text-white/40">7 giorni</div>
        </div>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto orio-h-scroll pb-1" data-testid="walk-weekly-pills">
        {weeklyPills.map((d) => (
          <div
            key={d.iso}
            className="shrink-0 flex flex-col items-center justify-center rounded-2xl text-center px-3 py-2 min-w-[54px] relative overflow-hidden"
            style={{
              background: d.isToday
                ? "linear-gradient(135deg, rgba(255,122,26,0.22) 0%, rgba(244,184,60,0.14) 100%)"
                : "rgba(255,255,255,0.06)",
              border: `1px solid ${d.isToday ? "rgba(255,122,26,0.55)" : "rgba(255,255,255,0.10)"}`,
              color: "white",
              boxShadow: d.isToday ? "0 10px 24px -12px rgba(255,122,26,0.65)" : "none",
            }}
          >
            {d.isToday && (
              <span aria-hidden
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,122,26,0.55) 0%, transparent 70%)" }} />
            )}
            <div className="text-[10px] uppercase tracking-widest" style={{ color: d.isToday ? "#F4B83C" : "rgba(255,255,255,0.55)" }}>{d.label}</div>
            <div className="font-display text-[18px] font-bold leading-none mt-1 tabular-nums" style={{ color: d.isToday ? "#FFEDD5" : "white" }}>
              {d.day}
            </div>
            <div className="mt-1 h-4 w-4 rounded-full flex items-center justify-center"
                 style={{
                   background: d.done
                     ? "linear-gradient(135deg,#FF7A1A,#F4B83C)"
                     : "rgba(255,255,255,0.10)",
                 }}>
              {d.done && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
            </div>
          </div>
        ))}
      </div>

      {/* Stats card — Apple Fitness style */}
      <div
        className="mt-6 rounded-3xl p-4 border"
        style={{
          background: "rgba(255,255,255,0.04)",
          borderColor: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">I tuoi progressi</div>
          {stats?.streak_day > 0 && (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-bold" style={{ color: "#FF7A1A" }}>
              <Flame className="h-3.5 w-3.5" /> {stats.streak_day}
              <span className="text-white/60 font-medium">giorni streak</span>
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <DarkStat value={String(walksWeek)} label="Uscite" />
          <DarkStat value={kmWeek} label="Km" accent="#FF7A1A" />
          <DarkStat value={String(kcalWeek)} label="Kcal" />
        </div>
      </div>

      {/* AI motivational bar — ORIO ring + AI-recommended chip.
          Text rotates each render based on time-of-day + streak. */}
      <div
        className="mt-5 rounded-3xl p-4 border flex items-center gap-3"
        style={{
          background: "rgba(255,255,255,0.04)",
          borderColor: "rgba(255,255,255,0.08)",
        }}
      >
        <div
          className="h-11 w-11 rounded-full shrink-0"
          style={{
            background:
              "conic-gradient(from 200deg, #FF7A1A, #F4B83C, #FF7A1A)",
            padding: 3,
          }}
        >
          <div className="w-full h-full rounded-full flex items-center justify-center"
               style={{ background: "#0B0806" }}>
            <Sparkles className="h-4 w-4" style={{ color: "#FF7A1A" }} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.20em] text-white/50">ORIO AI ti consiglia</div>
          <div className="text-[13px] text-white/95 leading-snug mt-0.5">
            {stats?.streak_day > 2
              ? `Bella striscia! Prova un ritmo un po' più veloce oggi.`
              : hour < 12
              ? `Mattinata perfetta per una camminata di 30 minuti con ${selectedDogs[0]?.name || "il tuo cane"}.`
              : hour < 19
              ? `Il caldo di pomeriggio? Cerca ombra e porta acqua.`
              : `Camminata serale: 20-30 minuti aiutano il sonno del tuo cane.`}
          </div>
        </div>
      </div>

      {/* Dog picker cards — dark version, multi-select */}
      {dogs.length > 1 && (
        <div className="mt-6">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">Con chi esci?</div>
            {selectedSet.size > 1 && (
              <div className="text-[10.5px] font-semibold" style={{ color: "#F4B83C" }}>
                {selectedSet.size} cani
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="walk-dog-picker">
            {dogs.map((d) => {
              const isSelected = selectedSet.has(d.id);
              return (
                <button
                  key={d.id}
                  onClick={() => toggleDog(d.id)}
                  data-testid={`walk-dog-${d.id}`}
                  className="rounded-2xl border p-3 text-left transition-all relative"
                  style={{
                    borderColor: isSelected ? "#FF7A1A" : "rgba(255,255,255,0.10)",
                    background: isSelected ? "rgba(255,122,26,0.12)" : "rgba(255,255,255,0.04)",
                    boxShadow: isSelected ? `0 12px 26px -14px #FF7A1A55` : "none",
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 h-5 w-5 rounded-full flex items-center justify-center"
                         style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                  <div className="h-14 w-14 rounded-2xl overflow-hidden bg-white/10 mb-2">
                    {d.photo_url && <img src={d.photo_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="text-[13.5px] font-semibold text-white leading-tight">{d.name}</div>
                  {d.breed && <div className="text-[11px] text-white/60 leading-tight mt-0.5">{d.breed}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Featured challenge card — sfida with dog cinema image */}
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50 mb-2">Sfida</div>
        <div
          className="relative rounded-3xl overflow-hidden p-5"
          style={{
            background: "linear-gradient(135deg, rgba(255,122,26,0.14) 0%, rgba(255,122,26,0.02) 100%)",
            border: "1px solid rgba(255,122,26,0.25)",
            minHeight: 210,
          }}
        >
          <div
            aria-hidden
            className="absolute right-0 top-0 bottom-0 w-[52%] pointer-events-none"
            style={{
              backgroundImage: `url(${orioIllustration("feat-walk")})`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 45%)",
              maskImage: "linear-gradient(90deg, transparent 0%, #000 45%)",
            }}
          />
          <div className="relative z-10 max-w-[62%]">
            <div className="inline-flex items-center gap-1 text-[10px] font-bold text-white uppercase tracking-widest px-2 py-1 rounded-full"
                 style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
              <Sparkles className="h-3 w-3" /> AI Recommended
            </div>
            <h3 className="mt-2 font-display text-[22px] text-white leading-tight" style={{ fontWeight: 700 }}>
              21km in 7 giorni
            </h3>
            <p className="text-[12.5px] text-white/70 mt-1">
              Sfida della settimana. +50 Karma e un badge esclusivo.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-white/85">
              <Route className="h-3.5 w-3.5" style={{ color: "#FF7A1A" }} />
              {kmWeek} / 21 km
            </div>
          </div>
        </div>
      </div>

      {/* Per-dog history — the existing panel, dark-adapted below */}
      <div className="mt-6">
        <PerDogHistoryPanel />
      </div>

      {/* Achievements strip — walk-related badges from /achievements/me */}
      <WalkAchievements />

      {/* Primary CTA — big pill Nike-style. Anchored above the mobile
          bottom nav so it never gets covered on any device. */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={() => selectedSet.size > 0 && setShowCoach(true)}
        disabled={starting || selectedSet.size === 0}
        data-testid="walk-start-btn"
        className="fixed left-1/2 -translate-x-1/2 z-40 w-[92vw] max-w-md inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-white font-semibold disabled:opacity-60"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
          background: "linear-gradient(135deg, #FF7A1A 0%, #E9542A 100%)",
          boxShadow: "0 22px 48px -14px rgba(233,84,42,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
        }}
      >
        {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" fill="white" />}
        {selectedDogs.length === 0
          ? "Scegli un cane"
          : selectedDogs.length === 1
            ? `Cammina con ${selectedDogs[0].name}`
            : `Cammina con ${selectedDogs.map((d) => d.name).join(" + ")}`}
      </motion.button>

      {/* Coach modal — GPS warmup + safety tips before the walk starts */}
      {showCoach && (
        <WalkCoachModal
          dogs={selectedDogs}
          onClose={() => setShowCoach(false)}
          onConfirm={() => {
            setShowCoach(false);
            onStart(Array.from(selectedSet));
          }}
          starting={starting}
        />
      )}
    </div>
  );
}

/* Compact dark stat used inside the Fitness-style dashboard card. */
function DarkStat({ value, label, accent }) {
  return (
    <div className="text-center">
      <div
        className="font-display text-2xl tabular-nums leading-none"
        style={{ color: accent || "white", fontWeight: 700 }}
      >
        {value}
      </div>
      <div className="mt-1 text-[10.5px] uppercase tracking-[0.20em] text-white/50">{label}</div>
    </div>
  );
}

function StatMini({ label, value, accent }) {
  return (
    <div className="rounded-2xl border p-3" style={{ background: `${accent}18`, borderColor: `${accent}2A` }}>
      <div className="font-display text-2xl tabular-nums font-bold leading-none" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/50 mt-1">{label}</div>
    </div>
  );
}

/* ---------- Live Tracker ---------- */
function LiveMapFollow({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const last = points[points.length - 1];
      map.setView([last.lat, last.lng], map.getZoom() || 16, { animate: true });
    }
  }, [points, map]);
  return null;
}

function LiveTracker({ walk, onEnd, ending }) {
  const [points, setPoints] = useState(walk.points || []);
  const [stats, setStats] = useState({
    distance_m: walk.distance_m || 0,
    duration_s: walk.duration_s || 0,
    calories_kcal_dog: walk.calories_kcal_dog || 0,
    calories_kcal_human: walk.calories_kcal_human || 0,
    pace_min_per_km: walk.pace_min_per_km || 0,
    avg_speed_kmh: walk.avg_speed_kmh || 0,
  });
  const [hazards, setHazards] = useState([]);
  const [peers, setPeers] = useState([]);
  const [meLoc, setMeLoc] = useState(null);
  const bufferRef = useRef([]);
  const startTsRef = useRef(new Date(walk.started_at).getTime());
  const [tick, setTick] = useState(0);

  // Live timer tick — updates every 500ms.
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(i);
  }, []);
  const elapsedS = Math.max(0, Math.floor((Date.now() - startTsRef.current) / 1000));

  // GPS watch — HARDENED precision pipeline. Rules (built after the user
  // explicitly asked for "massima precisione, basata sull'andatura, no auto"):
  //  • GRACE PERIOD: discard first 3 seconds — GPS cold-start can jump
  //    hundreds of meters before stabilising. The coach modal already
  //    waits for a lock, this is belt-and-braces.
  //  • ACCURACY FILTER: reject any reading with accuracy > 25m.
  //  • SPEED FILTER: reject instantaneous speed > 20 km/h (likely car).
  //    Three consecutive >20 km/h readings → auto-pause with toast.
  //  • JITTER FILTER: reject any point < 2m from the previous one (GPS
  //    standing-still jitter — would inflate distance by hundreds of m).
  //  • Keep a smoothed speed for display (last 3-point moving average).
  const transportStreakRef = useRef(0);
  const pausedRef = useRef(false);
  const lastKeptRef = useRef(null);  // {lat,lng,ts}
  const warmupStartRef = useRef(Date.now());
  const speedWindowRef = useRef([]);  // last 3 km/h values for smoothing
  const [liveSpeedKmh, setLiveSpeedKmh] = useState(0);
  const [paused, setPaused] = useState(false);
  const [rejectedCount, setRejectedCount] = useState(0);

  useEffect(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non disponibile");
      return;
    }
    warmupStartRef.current = Date.now();
    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        const now = Date.now();
        const sinceStart = now - warmupStartRef.current;
        const accuracy = p.coords.accuracy ?? 999;
        const browserSpeed = p.coords.speed != null ? p.coords.speed * 3.6 : null;

        // 1) GRACE PERIOD — first 3s discarded entirely
        if (sinceStart < 3000) {
          setRejectedCount((c) => c + 1);
          return;
        }
        // 2) ACCURACY FILTER
        if (accuracy > 25) {
          setRejectedCount((c) => c + 1);
          return;
        }
        // 3) Build candidate point
        const pt = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          ts: new Date(now).toISOString(),
          accuracy_m: accuracy,
          speed_kmh: browserSpeed,
        };
        setMeLoc({ lat: pt.lat, lng: pt.lng });

        // 4) JITTER FILTER — reject if < 2m from previous kept point
        const last = lastKeptRef.current;
        let segSpeedKmh = browserSpeed;
        if (last) {
          const dMeters = distanceM(last, pt);
          const dtSec = Math.max(0.001, (now - last.ts_ms) / 1000);
          const computedKmh = (dMeters / dtSec) * 3.6;
          // If the OS doesn't report speed, use the segment speed
          if (segSpeedKmh == null) segSpeedKmh = computedKmh;
          // Jitter: too close in distance to be a real movement
          if (dMeters < 2) {
            setRejectedCount((c) => c + 1);
            return;
          }
          // 5) SPEED FILTER — reject implausible jumps and track car-like streaks
          if (segSpeedKmh > 50) {
            // Hard reject: physically impossible for walk/run
            setRejectedCount((c) => c + 1);
            return;
          }
          if (segSpeedKmh > 20) {
            transportStreakRef.current += 1;
            if (transportStreakRef.current >= 3 && !pausedRef.current) {
              pausedRef.current = true;
              setPaused(true);
              hapticWarning();
              toast.message("Pausa automatica · velocità da auto rilevata (>20 km/h)", {
                description: "Rallenta e riprenderemo a tracciare quando torni a passo di cane.",
              });
            }
            // Still reject the point itself
            setRejectedCount((c) => c + 1);
            return;
          } else {
            // Good walking/running speed → resume if paused
            transportStreakRef.current = 0;
            if (pausedRef.current) {
              pausedRef.current = false;
              setPaused(false);
              toast.success("Tracciamento ripreso");
            }
          }
        }

        // 6) Keep point
        lastKeptRef.current = { lat: pt.lat, lng: pt.lng, ts_ms: now };

        // 7) Smoothed live speed (last 3 kept segments)
        if (segSpeedKmh != null && segSpeedKmh >= 0) {
          const w = speedWindowRef.current;
          w.push(segSpeedKmh);
          if (w.length > 3) w.shift();
          const avg = w.reduce((s, v) => s + v, 0) / w.length;
          setLiveSpeedKmh(avg);
        }

        setPoints((prev) => {
          const nxt = [...prev, pt];
          return nxt.length > 5000 ? nxt.slice(-5000) : nxt;
        });
        bufferRef.current.push(pt);
      },
      (err) => console.warn("geo err", err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
    const flush = setInterval(async () => {
      if (bufferRef.current.length === 0) return;
      const batch = bufferRef.current.splice(0, bufferRef.current.length);
      try {
        const { data } = await api.post(`/walks/${walk.id}/points`, { points: batch });
        if (data && typeof data.distance_m === "number") setStats(data);
      } catch { /* offline mode → local trail stays */ }
    }, 6000);
    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(flush);
    };
  }, [walk.id]);

  // Hazards + live peers polling — refresh every 45s using current position.
  // Also detects hazards within 200m of the user (real-time) and fires a
  // haptic + toast alert the FIRST time each hazard enters the proximity ring,
  // so the walker gets a heads-up before physically arriving on the spot.
  const alertedHazardsRef = useRef(new Set());
  const [nearbyHazard, setNearbyHazard] = useState(null); // {id, label, distance_m}
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      const loc = meLoc || (points.length > 0
        ? { lat: points[points.length - 1].lat, lng: points[points.length - 1].lng }
        : null);
      if (!loc) return;
      try {
        const [{ data: mapPoints }, peersRes] = await Promise.all([
          api.get("/map/points"),
          api.get("/missioni/visible-users", { params: { lat: loc.lat, lng: loc.lng, radius_km: 5 } })
             .catch(() => ({ data: { users: [] } })),
        ]);
        if (cancel) return;
        const allHazards = (mapPoints || []).filter((p) => p.type === "hazard");
        // 3km ring for map display.
        const near3km = allHazards.filter((h) => distanceM(loc, h) < 3000);
        setHazards(near3km);
        setPeers(peersRes?.data?.users || []);

        // Proximity ring: 200m — fire haptic + toast + banner ONCE per hazard.
        const veryClose = allHazards
          .map((h) => ({ ...h, distance_m: distanceM(loc, h) }))
          .filter((h) => h.distance_m < 200)
          .sort((a, b) => a.distance_m - b.distance_m);

        if (veryClose.length > 0) {
          const closest = veryClose[0];
          setNearbyHazard({
            id: closest.id,
            label: closest.label || closest.title || "Pericolo",
            severity: closest.severity || closest.color || "#E94545",
            distance_m: Math.round(closest.distance_m),
          });
          // Fire per-hazard alert only the first time.
          for (const h of veryClose) {
            if (!alertedHazardsRef.current.has(h.id)) {
              alertedHazardsRef.current.add(h.id);
              hapticWarning();
              toast.warning(
                `⚠️ ${h.label || h.title || "Pericolo"} a ${Math.round(h.distance_m)}m`,
                { duration: 6000 }
              );
            }
          }
        } else {
          setNearbyHazard(null);
        }
      } catch { /* ignore */ }
    };
    load();
    // Poll more aggressively during walk so 200m alerts fire quickly.
    const i = setInterval(load, 20000);
    return () => { cancel = true; clearInterval(i); };
  }, [meLoc, points]);

  const initialCenter = useMemo(() => {
    if (points.length > 0) return [points[0].lat, points[0].lng];
    return [41.9028, 12.4964];
  }, [points]);
  const path = useMemo(() => points.map((p) => [p.lat, p.lng]), [points]);
  const last = points[points.length - 1];

  const km = stats.distance_m / 1000;
  const minutes = Math.floor(elapsedS / 60);
  const kcalDog = stats.calories_kcal_dog;

  return (
    <div className="fixed inset-0 z-[500] flex flex-col text-white" data-testid="walk-live"
         style={{
           background:
             "radial-gradient(140% 60% at 0% 0%, rgba(255,122,26,0.22) 0%, rgba(255,122,26,0) 55%), " +
             "radial-gradient(120% 60% at 100% 100%, rgba(52,211,153,0.14) 0%, rgba(52,211,153,0) 55%), " +
             "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
         }}>
      {/* Map — 40vh with hazard + peer overlay */}
      <div className="relative" style={{ height: "40vh", minHeight: 240 }}>
        <MapContainer
          center={initialCenter}
          zoom={16}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          attributionControl={false}
          scrollWheelZoom={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {path.length > 1 && (
            <Polyline positions={path}
                      pathOptions={{ color: "#FF7A1A", weight: 5, opacity: 0.95, lineCap: "round", lineJoin: "round" }} />
          )}
          {hazards.map((h) => (
            <Marker key={h.id} position={[h.lat, h.lng]} icon={makeHazardIcon(h.color, "!")} />
          ))}
          {peers.map((u) => (
            u.lat && u.lng && (
              <Marker key={u.id} position={[u.lat, u.lng]} icon={makePeerIcon(u.photo_url, u.name)} />
            )
          ))}
          {last && <Marker position={[last.lat, last.lng]} icon={dogIcon} />}
          <LiveMapFollow points={points} />
        </MapContainer>
        {/* Header pills — glass-dark on the map */}
        <div className="absolute top-0 inset-x-0 z-[500] p-3 flex items-center justify-between pointer-events-none"
             style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
          <div
            className="px-3 py-1.5 rounded-full text-[11px] font-semibold text-white inline-flex items-center gap-1.5"
            style={{
              background: "rgba(11,8,6,0.65)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.14)",
              boxShadow: "0 6px 18px -4px rgba(0,0,0,0.35)",
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: "#E9542A" }} />
            Con {walk.dog_name || "il tuo cane"}
          </div>
          <div className="flex flex-col gap-1.5 items-end">
            {nearbyHazard && (
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1.5 orio-pulse"
                style={{
                  color: "#fff",
                  background: "linear-gradient(135deg, rgba(233,69,69,0.95) 0%, rgba(255,92,42,0.95) 100%)",
                  border: "1px solid rgba(255,255,255,0.35)",
                  boxShadow: "0 14px 28px -10px rgba(233,69,69,0.75)",
                }}
                data-testid="walk-nearby-hazard-alert"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {nearbyHazard.label} · {nearbyHazard.distance_m}m
              </motion.div>
            )}
            {hazards.length > 0 && (
              <div
                className="px-2.5 py-1 rounded-full text-[10.5px] font-semibold inline-flex items-center gap-1"
                style={{
                  color: "#FF9E45",
                  background: "rgba(233,84,42,0.20)",
                  border: "1px solid rgba(233,84,42,0.45)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="walk-hazards-pill"
              >
                <AlertTriangle className="h-3 w-3" /> {hazards.length}
              </div>
            )}
            {peers.length > 0 && (
              <div
                className="px-2.5 py-1 rounded-full text-[10.5px] font-semibold inline-flex items-center gap-1"
                style={{
                  color: "#34D399",
                  background: "rgba(52,211,153,0.15)",
                  border: "1px solid rgba(52,211,153,0.35)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="walk-peers-pill"
              >
                <Users className="h-3 w-3" /> {peers.length}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom sheet — Apple-Watch style rings on jet black */}
      <div className="flex-1 relative overflow-y-auto"
           style={{
             background: "linear-gradient(180deg, rgba(11,8,6,0.90) 0%, #050403 100%)",
             borderTop: "1px solid rgba(255,255,255,0.08)",
           }}>
        {/* Grabber */}
        <div className="pt-3 pb-1 flex justify-center">
          <div className="h-1 w-10 rounded-full bg-white/20" />
        </div>

        {/* Rings + center readout */}
        <div className="mt-2 flex flex-col items-center relative">
          <div className="relative">
            <OrioRings km={km} minutes={minutes} kcal={kcalDog} size={240} dark />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[9.5px] uppercase tracking-[0.24em] text-white/50">Distanza</div>
              <div className="flex items-baseline gap-1 mt-0.5">
                <AnimatedNumber
                  value={km}
                  format={(v) => v.toFixed(2)}
                  className="font-display font-bold tabular-nums leading-none text-white"
                  style={{ fontSize: "44px", letterSpacing: "-0.02em" }}
                />
                <span className="text-[14px] text-white/60 font-medium">km</span>
              </div>
              <div className="text-[10.5px] text-white/60 mt-0.5">
                <span style={{ color: "#F4B83C" }}>{fmtHMS(elapsedS)}</span>
                <span className="mx-1.5">·</span>
                <span style={{ color: "#34D399" }}>
                  <AnimatedNumber value={kcalDog} format={(v) => Math.round(v).toString()} />
                  {" kcal"}
                </span>
              </div>
            </div>
          </div>
          {/* Ring legend */}
          <div className="mt-3 flex items-center gap-3 text-[10px] text-white/70">
            <LegendDot color="#FF7A1A" label={`km · goal ${GOAL_KM}`} />
            <LegendDot color="#F4B83C" label={`min · goal ${GOAL_MIN}`} />
            <LegendDot color="#34D399" label={`kcal · goal ${GOAL_KCAL}`} />
          </div>
        </div>

        {/* Secondary metrics — 3 cards */}
        <div className="mt-4 px-4 grid grid-cols-3 gap-2">
          <MetricCard
            testid="walk-metric-pace"
            label="Andatura"
            value={fmtPace(stats.pace_min_per_km)}
            sub="min/km"
            icon={TrendingUp}
            accent="#34D399"
            dark
          />
          <MetricCard
            testid="walk-metric-speed"
            label="Velocità"
            value={<AnimatedNumber value={stats.avg_speed_kmh} format={(v) => v.toFixed(1)} />}
            sub="km/h"
            icon={Zap}
            accent="#F4B83C"
            dark
          />
          <MetricCard
            testid="walk-metric-kcal-human"
            label="Kcal tue"
            value={<AnimatedNumber value={stats.calories_kcal_human} format={(v) => Math.round(v).toString()} />}
            icon={Flame}
            accent="#FF7A1A"
            dark
          />
        </div>

        {/* ─── NEARBY WALK BUDDIES BAR ─── */}
        {peers.length > 0 && (
          <NearbyBuddiesBar peers={peers} meLoc={meLoc} />
        )}

        {/* End button */}
        <div className="px-4 mt-5"
             style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onEnd}
            disabled={ending}
            data-testid="walk-end-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full text-white font-semibold disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #E94545 0%, #FF7A1A 100%)",
              boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
            }}
          >
            {ending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Square className="h-5 w-5" fill="white" />}
            Termina passeggiata
          </motion.button>
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="inline-flex items-center gap-1 text-[#7A7A7A]">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   NearbyBuddiesBar — strip of avatars of online peers within 10km
   during the walk. Tap on the bar to open an opaque sheet listing
   each peer with distance + a 👋 greet button. ───────────────── */
function NearbyBuddiesBar({ peers, meLoc }) {
  const [open, setOpen] = useState(false);
  const [sentIds, setSentIds] = useState(() => new Set());
  const enriched = useMemo(() => {
    if (!meLoc) return peers.map((p) => ({ ...p, dist_km: null }));
    return peers
      .map((p) => {
        const lat = p.lat ?? p.last_lat;
        const lng = p.lng ?? p.last_lng;
        if (lat == null || lng == null) return { ...p, dist_km: null };
        const d = distanceM({ lat: meLoc.lat, lng: meLoc.lng }, { lat, lng }) / 1000;
        return { ...p, dist_km: d };
      })
      .sort((a, b) => (a.dist_km ?? 999) - (b.dist_km ?? 999));
  }, [peers, meLoc]);

  const sendGreeting = async (peer) => {
    if (sentIds.has(peer.id)) return;
    hapticTap();
    try {
      await api.post("/presence/greet", { to_user_id: peer.id });
      setSentIds((s) => new Set([...s, peer.id]));
      toast.success(`Saluto inviato a ${peer.name || "amico"} 👋`);
    } catch {
      // Fallback: try notifications endpoint
      try {
        await api.post("/notifications/send", {
          user_id: peer.id,
          type: "greeting",
          title: "Un saluto durante la passeggiata",
          body: `${peer.name || "Un amico"} ti saluta dal suo ORIO Walk 👋`,
        });
        setSentIds((s) => new Set([...s, peer.id]));
        toast.success(`Saluto inviato a ${peer.name || "amico"} 👋`);
      } catch {
        toast.error("Non ho potuto inviare il saluto");
      }
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => { hapticTap(); setOpen(true); }}
        data-testid="walk-buddies-bar"
        className="mx-4 mt-4 w-[calc(100%-32px)] rounded-2xl p-3 border inline-flex items-center justify-between gap-3"
        style={{
          background: "rgba(52,211,153,0.08)",
          borderColor: "rgba(52,211,153,0.28)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex -space-x-2 shrink-0">
            {enriched.slice(0, 4).map((p) => (
              <div key={p.id}
                   className="h-7 w-7 rounded-full border-2 overflow-hidden"
                   style={{ borderColor: "#0B0806", background: "rgba(52,211,153,0.25)" }}>
                {p.photo_url
                  ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-white">
                      {(p.name || "?").charAt(0).toUpperCase()}
                    </div>}
              </div>
            ))}
          </div>
          <div className="min-w-0 text-left">
            <div className="text-[13px] font-semibold text-white leading-tight">
              {enriched.length} {enriched.length === 1 ? "amico vicino" : "amici vicini"} in walk
            </div>
            <div className="text-[11px] text-white/60 leading-tight mt-0.5 truncate">
              Tap per vedere stats e salutare
            </div>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-white/50 shrink-0" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1050] flex items-end sm:items-center justify-center p-3"
            onClick={() => setOpen(false)}
            data-testid="walk-buddies-sheet"
          >
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(22px)" }} />
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-t-[28px] sm:rounded-[28px] overflow-hidden border text-white max-h-[70vh] flex flex-col"
              style={{
                background: "linear-gradient(180deg, #1A1512 0%, #0B0806 100%)",
                borderColor: "rgba(255,255,255,0.12)",
              }}>
              <div className="p-5 pb-3 flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">In walk vicino a te</div>
                  <h3 className="font-display text-2xl mt-0.5">{enriched.length} amic{enriched.length === 1 ? "o" : "i"}</h3>
                </div>
                <button onClick={() => setOpen(false)}
                        className="h-8 w-8 rounded-full border border-white/15 flex items-center justify-center"
                        aria-label="Chiudi">
                  <X className="h-4 w-4 text-white/85" />
                </button>
              </div>
              <div className="px-3 pb-4 overflow-y-auto space-y-2">
                {enriched.map((p) => {
                  const sent = sentIds.has(p.id);
                  return (
                    <div key={p.id}
                         data-testid={`buddy-row-${p.id}`}
                         className="rounded-2xl border p-3 flex items-center gap-3"
                         style={{
                           background: "rgba(255,255,255,0.04)",
                           borderColor: "rgba(255,255,255,0.10)",
                         }}>
                      <div className="h-12 w-12 rounded-2xl overflow-hidden shrink-0"
                           style={{ background: "rgba(255,255,255,0.08)" }}>
                        {p.photo_url
                          ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-[16px] font-bold text-white">
                              {(p.name || "?").charAt(0).toUpperCase()}
                            </div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-semibold text-white leading-tight truncate">
                          {p.name || "Amico"}
                        </div>
                        <div className="text-[11.5px] text-white/60 leading-tight mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                          {p.dist_km != null && (
                            <span className="inline-flex items-center gap-0.5">
                              <MapPin className="h-3 w-3" />
                              {p.dist_km < 1 ? `${Math.round(p.dist_km * 1000)}m` : `${p.dist_km.toFixed(1)}km`}
                            </span>
                          )}
                          {p.dog_name && (
                            <>
                              <span className="text-white/30">·</span>
                              <span className="inline-flex items-center gap-0.5">
                                <PawPrint className="h-3 w-3" /> {p.dog_name}
                              </span>
                            </>
                          )}
                          {p.walk_distance_km != null && (
                            <>
                              <span className="text-white/30">·</span>
                              <span>{Number(p.walk_distance_km).toFixed(1)}km oggi</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => sendGreeting(p)}
                        disabled={sent}
                        data-testid={`buddy-greet-${p.id}`}
                        className="px-3 py-2 rounded-full text-[11.5px] font-bold inline-flex items-center gap-1 disabled:opacity-60"
                        style={{
                          background: sent
                            ? "rgba(52,211,153,0.15)"
                            : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                          color: sent ? "#34D399" : "white",
                          border: sent ? "1px solid rgba(52,211,153,0.35)" : "none",
                        }}>
                        {sent ? <><Check className="h-3 w-3" strokeWidth={3} /> Inviato</> : <>👋 Saluta</>}
                      </button>
                    </div>
                  );
                })}
                {enriched.length === 0 && (
                  <div className="text-center text-white/60 text-[13px] py-6">
                    Nessun amico vicino al momento 🐾
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MetricCard({ label, value, sub, icon: Icon, accent, testid, dark = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", damping: 22, stiffness: 260 }}
      className="rounded-2xl border p-3.5 text-center relative overflow-hidden"
      style={{
        background: dark ? "rgba(255,255,255,0.05)" : "white",
        borderColor: dark ? "rgba(255,255,255,0.10)" : C.border,
        boxShadow: dark ? "none" : "0 6px 16px -8px rgba(0,0,0,0.10)",
        backdropFilter: dark ? "blur(12px)" : undefined,
      }}
      data-testid={testid}
    >
      <div className="h-7 w-7 rounded-lg mx-auto mb-1.5 flex items-center justify-center"
           style={{ background: `${accent}22`, color: accent }}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="font-display font-bold tabular-nums text-[20px] leading-none"
           style={{ color: dark ? "#fff" : "#0F0F0F" }}>
        {value}
      </div>
      {sub && (
        <div className="text-[9.5px] uppercase tracking-[0.14em] mt-1"
             style={{ color: dark ? "rgba(255,255,255,0.45)" : "#9A9A9A" }}>
          {sub}
        </div>
      )}
      <div className="text-[10px] mt-1.5"
           style={{ color: dark ? "rgba(255,255,255,0.55)" : "#7A7A7A" }}>
        {label}
      </div>
    </motion.div>
  );
}

/* ---------- Summary (post-walk) — Apple Fitness dark style ---------- */
function Summary({ walk, onDone }) {
  const path = (walk.points || []).map((p) => [p.lat, p.lng]);
  const bounds = path.length > 1 ? path : null;
  const kcal = (walk.calories_kcal_dog || 0) + (walk.calories_kcal_human || 0);
  const km = (walk.distance_m || 0) / 1000;
  const minutes = Math.floor((walk.duration_s || 0) / 60);
  return (
    <div
      className="min-h-[100dvh] px-4 pt-6 pb-[220px] md:pb-32 max-w-md mx-auto text-white relative"
      style={{
        background:
          "radial-gradient(140% 60% at 0% 0%, rgba(255,122,26,0.22) 0%, rgba(255,122,26,0) 55%), " +
          "radial-gradient(120% 60% at 100% 100%, rgba(52,211,153,0.14) 0%, rgba(52,211,153,0) 55%), " +
          "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
      }}
      data-testid="walk-summary"
    >
      <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/55 inline-flex items-center gap-1.5">
        <Sparkles className="h-3 w-3" style={{ color: "#F4B83C" }} /> Ottimo lavoro!
      </div>
      <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-1 text-white">
        <span className="orio-grad-text">{km.toFixed(2)} km</span><br />
        con {walk.dog_name || "il tuo cane"}
      </h1>
      <p className="mt-2 text-[13.5px] text-white/60">
        {fmtHMS(walk.duration_s || 0)} · andatura {fmtPace(walk.pace_min_per_km)} · {Math.round(kcal)} kcal totali
      </p>

      {/* Rings — dark, filled to actual values */}
      <div className="mt-6 flex justify-center">
        <OrioRings km={km} minutes={minutes} kcal={walk.calories_kcal_dog || 0} size={220} dark />
      </div>

      {/* Karma & streak banner — warm glow on jet black */}
      {(walk.karma_awarded || 0) > 0 && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="mt-5 rounded-2xl p-4 border relative overflow-hidden"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,122,26,0.22) 0%, rgba(244,184,60,0.18) 55%, rgba(233,84,42,0.20) 100%)",
            borderColor: "rgba(255,122,26,0.45)",
            boxShadow: "0 22px 44px -18px rgba(255,122,26,0.45)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-white"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)",
                boxShadow: "0 10px 22px -8px rgba(255,122,26,0.55)",
              }}>
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "#FFC98A" }}>
                Karma guadagnato
              </div>
              <div className="font-display text-2xl leading-none tabular-nums text-white mt-1">
                +{walk.karma_awarded}
              </div>
            </div>
            {walk.streak_day > 0 && (
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/55">Streak</div>
                <div className="font-display text-2xl text-white tabular-nums inline-flex items-center gap-1">
                  <Flame className="h-5 w-5" style={{ color: C.orange }} />{walk.streak_day}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Map preview — dark bezel */}
      {path.length > 1 && (
        <div
          className="mt-5 rounded-3xl overflow-hidden border relative"
          style={{
            borderColor: "rgba(255,255,255,0.12)",
            height: 260,
            boxShadow: "0 24px 48px -20px rgba(0,0,0,0.65)",
          }}
        >
          <MapContainer bounds={bounds} style={{ height: "100%", width: "100%" }}
                        zoomControl={false} attributionControl={false} scrollWheelZoom={false}
                        dragging={false} doubleClickZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Polyline positions={path}
                      pathOptions={{ color: C.orange, weight: 5, opacity: 0.9, lineCap: "round", lineJoin: "round" }} />
            {path[0] && <Marker position={path[0]} icon={dogIcon} />}
            {path[path.length - 1] && <Marker position={path[path.length - 1]} icon={dogIcon} />}
          </MapContainer>
          {/* Subtle vignette to blend the map with the dark background */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(120% 80% at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%)",
            }}
          />
        </div>
      )}

      {/* Detailed stats grid — dark glass cards */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <SumStat testid="sum-time" label="Tempo" value={fmtHMS(walk.duration_s || 0)} icon={Timer} accent={C.blue} />
        <SumStat testid="sum-pace" label="Andatura" value={fmtPace(walk.pace_min_per_km)} sub="min/km" icon={TrendingUp} accent={C.green} />
        <SumStat testid="sum-kcal-dog" label={`Kcal ${walk.dog_name || "cane"}`} value={String(walk.calories_kcal_dog || 0)} icon={Flame} accent={C.orange} />
        <SumStat testid="sum-kcal-you" label="Kcal tue" value={String(walk.calories_kcal_human || 0)} icon={Zap} accent={C.amber} />
        <SumStat testid="sum-speed" label="Velocità media" value={`${(walk.avg_speed_kmh || 0).toFixed(1)}`} sub="km/h" icon={TrendingUp} accent={C.blue} />
        <SumStat testid="sum-max-speed" label="Velocità max" value={`${(walk.max_speed_kmh || 0).toFixed(1)}`} sub="km/h" icon={TrendingUp} accent={C.red} />
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onDone}
        data-testid="walk-summary-close"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full text-white font-semibold"
        style={{
          background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
          boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
        }}
      >
        <Trophy className="h-4 w-4" /> Continua
      </motion.button>
    </div>
  );
}

/* Dark glass stat card — matches the LiveTracker MetricCard aesthetic. */
function SumStat({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div
      className="rounded-2xl border p-4 relative overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.05)",
        borderColor: "rgba(255,255,255,0.10)",
        backdropFilter: "blur(12px)",
      }}
      data-testid={testid}
    >
      <div
        className="h-8 w-8 rounded-lg flex items-center justify-center mb-2"
        style={{ background: `${accent}26`, color: accent, border: `1px solid ${accent}55` }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-display font-bold tabular-nums text-2xl leading-none text-white">
        {value}
        {sub && <span className="text-sm text-white/45 ml-1 font-normal">{sub}</span>}
      </div>
      <div className="text-[11px] text-white/55 mt-1.5">{label}</div>
    </div>
  );
}

/* ---------- Main page ---------- */
export default function WalkingTracker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [active, setActive] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [stats, setStats] = useState(null);

  const bootstrap = async () => {
    setLoading(true);
    try {
      const [{ data: dogsData }, { data: activeData }, { data: statsData }] = await Promise.all([
        api.get("/dogs"),
        api.get("/walks/active"),
        api.get("/walks/stats"),
      ]);
      const real = (dogsData || []).filter((d) => !d.is_stub);
      setDogs(real);
      if (activeData?.active) setActive(activeData);
      setStats(statsData);
    } catch (e) {
      console.warn("walk bootstrap", e);
    } finally { setLoading(false); }
  };
  useEffect(() => { bootstrap(); }, []);

  const startWalk = async (dogIds) => {
    // Backward-compatible: accept either a single string ID or an array.
    const ids = Array.isArray(dogIds) ? dogIds : [dogIds];
    const primaryId = ids[0];
    setStarting(true);
    try {
      const { data } = await api.post("/walks/start", { dog_id: primaryId, dog_ids: ids });
      const primary = dogs.find((d) => d.id === primaryId);
      const extra = ids.slice(1).map((id) => dogs.find((d) => d.id === id)).filter(Boolean);
      hapticSuccess();
      setActive({
        id: data.walk_id,
        dog_id: primaryId,
        dog_name: primary?.name,
        dog_photo_url: primary?.photo_url,
        dog_ids: ids,
        extra_dogs: extra,
        started_at: data.started_at,
        status: "active",
        points: [],
        distance_m: 0, duration_s: 0,
        calories_kcal_dog: 0, calories_kcal_human: 0,
        pace_min_per_km: 0, avg_speed_kmh: 0,
      });
      toast.success(
        ids.length > 1
          ? `Passeggiata iniziata con ${primary?.name} + ${extra.length} ${extra.length === 1 ? "cane" : "cani"}!`
          : "Passeggiata iniziata!"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile iniziare");
    } finally { setStarting(false); }
  };

  const endWalk = async () => {
    if (!active) return;
    setEnding(true);
    try {
      const { data } = await api.post(`/walks/${active.id}/end`);
      hapticSuccess();
      try {
        const { data: full } = await api.get(`/walks/${active.id}`);
        setCompleted({ ...data, ...full });
      } catch { setCompleted(data); }
      setActive(null);
      try { const { data: s } = await api.get("/walks/stats"); setStats(s); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore terminazione");
    } finally { setEnding(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center text-[#9A9A9A]" data-testid="walk-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carico…
      </div>
    );
  }
  if (dogs.length === 0) {
    return (
      <div className="min-h-[80vh] px-6 pt-10 max-w-md mx-auto" data-testid="walk-no-dog">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A]">ORIO Walk</div>
        <h1 className="font-display text-3xl mt-1">Aggiungi prima un cane</h1>
        <p className="text-[13px] text-[#6B6B6B] mt-2">Serve almeno un passaporto per iniziare a tracciare le passeggiate.</p>
        <button onClick={() => navigate("/app/cani/nuovo")}
                className="mt-5 orio-cta-btn px-5 py-3 rounded-full text-black text-sm font-semibold">
          Crea passaporto
        </button>
      </div>
    );
  }

  if (completed) {
    return <Summary walk={completed} onDone={() => { setCompleted(null); navigate("/app"); }} />;
  }
  if (active) {
    return <LiveTracker walk={active} onEnd={endWalk} ending={ending} />;
  }
  return <StartPanel dogs={dogs} onStart={startWalk} starting={starting} stats={stats} />;
}

/* ============================================================
   PerDogHistoryPanel — click a dog to expand, then click a walk
   to see its full detail (mini map + stats) inline.
   ============================================================ */
function PerDogHistoryPanel() {
  const [data, setData] = useState(null);
  const [openDog, setOpenDog] = useState(null);
  const [openWalk, setOpenWalk] = useState(null);      // walk id of the expanded row
  const [walkDetails, setWalkDetails] = useState({});  // cache: id -> full walk

  useEffect(() => {
    api.get("/walks/stats-by-dog")
      .then((r) => setData(r.data))
      .catch(() => setData({ dogs: [] }));
  }, []);

  const loadWalk = async (wid) => {
    if (walkDetails[wid]) { setOpenWalk(openWalk === wid ? null : wid); return; }
    try {
      const { data: full } = await api.get(`/walks/${wid}`);
      setWalkDetails((prev) => ({ ...prev, [wid]: full }));
      setOpenWalk(wid);
    } catch { toast.error("Impossibile caricare la passeggiata"); }
  };

  if (!data || (data.dogs?.length ?? 0) === 0) return null;
  const dogsWithWalks = data.dogs.filter((d) => (d.stats?.count || 0) > 0);
  if (dogsWithWalks.length === 0) return null;

  return (
    <div className="mt-6 rounded-3xl p-5 border"
         style={{
           background: "rgba(255,255,255,0.04)",
           borderColor: "rgba(255,255,255,0.08)",
           backdropFilter: "blur(12px)",
         }}
         data-testid="per-dog-history">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">Storia per cane</div>
        <div className="text-[10px] text-white/40">Tocca per dettaglio</div>
      </div>
      <div className="space-y-2">
        {dogsWithWalks.map((item) => {
          const d = item.dog;
          const s = item.stats || {};
          const open = openDog === d.id;
          const km = ((s.distance_m || 0) / 1000).toFixed(1);
          const hours = ((s.duration_s || 0) / 3600).toFixed(1);
          return (
            <div key={d.id} className="rounded-2xl border transition-all"
                 style={{
                   borderColor: open ? "rgba(255,122,26,0.55)" : "rgba(255,255,255,0.10)",
                   background: open ? "rgba(255,122,26,0.08)" : "rgba(255,255,255,0.03)",
                 }}>
              <button onClick={() => { setOpenDog(open ? null : d.id); setOpenWalk(null); }}
                data-testid={`dog-history-${d.id}`}
                className="w-full flex items-center gap-3 p-3 text-left">
                <div className="h-11 w-11 rounded-2xl overflow-hidden shrink-0"
                     style={{ background: "rgba(255,255,255,0.08)" }}>
                  {d.photo_url && <img src={d.photo_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-white leading-tight truncate">{d.name}</div>
                  <div className="text-[11px] text-white/60 leading-tight mt-0.5">
                    {s.count} uscite · {km} km totali
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[15px] font-bold" style={{ color: "#FF7A1A" }}>
                    {km}<span className="text-[10px] font-medium text-white/50 ml-0.5">km</span>
                  </div>
                  <div className="text-[10px] text-white/50">{hours}h</div>
                </div>
                <ChevronDown className={`h-4 w-4 text-white/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
                    style={{ overflow: "hidden" }}>
                    <div className="px-3 pb-3">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <StatMini label="Uscite" value={String(s.count || 0)} accent={C.green} />
                        <StatMini label="Km tot" value={km} accent={C.orange} />
                        <StatMini label="Kcal" value={String(Math.round(s.dog_kcal || 0))} accent={C.amber} />
                      </div>
                      {item.recent?.length > 0 && (
                        <>
                          <div className="text-[9.5px] uppercase tracking-widest text-white/50 mt-2 mb-1">
                            Ultime {item.recent.length}
                          </div>
                          <div className="space-y-1">
                            {item.recent.map((w) => (
                              <WalkRow
                                key={w.id}
                                w={w}
                                open={openWalk === w.id}
                                onOpen={() => loadWalk(w.id)}
                                detail={walkDetails[w.id]}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Row for a single past walk — click to expand inline with mini map + stats. */
function WalkRow({ w, open, onOpen, detail }) {
  const wkm = ((w.distance_m || 0) / 1000).toFixed(2);
  const wm = Math.round((w.duration_s || 0) / 60);
  const dt = w.started_at ? new Date(w.started_at) : null;
  const path = (detail?.points || []).map((p) => [p.lat, p.lng]);
  const bounds = path.length > 1 ? path : null;

  return (
    <div className="rounded-lg overflow-hidden"
         style={{
           background: open ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.03)",
           border: "1px solid rgba(255,255,255,0.08)",
         }}>
      <button onClick={onOpen}
              data-testid={`walk-row-${w.id}`}
              className="w-full flex items-center justify-between px-2.5 py-2 text-left">
        <span className="text-[11px] text-white/55">
          {dt ? dt.toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
        <span className="text-[11px] font-semibold text-white">{wkm} km · {wm} min</span>
        <span className="inline-flex items-center gap-1.5">
          {(w.karma_awarded || 0) > 0 && (
            <span className="text-[10px]" style={{ color: C.orange }}>+{w.karma_awarded}⚡</span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && detail && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
            data-testid={`walk-row-detail-${w.id}`}>
            <div className="px-2.5 pb-2.5">
              {path.length > 1 && (
                <div className="rounded-xl overflow-hidden border mt-1" style={{ borderColor: "rgba(255,255,255,0.10)", height: 140 }}>
                  <MapContainer bounds={bounds} style={{ height: "100%", width: "100%" }}
                                zoomControl={false} attributionControl={false} scrollWheelZoom={false}
                                dragging={false} doubleClickZoom={false} touchZoom={false}>
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png" />
                    <Polyline positions={path} pathOptions={{ color: C.orange, weight: 4, opacity: 0.9, lineCap: "round" }} />
                    {path[0] && <Marker position={path[0]} icon={dogIcon} />}
                    {path[path.length - 1] && <Marker position={path[path.length - 1]} icon={dogIcon} />}
                  </MapContainer>
                </div>
              )}
              <div className="mt-2 grid grid-cols-4 gap-1.5 text-center">
                <MiniStat label="km" value={wkm} accent={C.orange} />
                <MiniStat label="min" value={String(wm)} accent={C.green} />
                <MiniStat label="kcal" value={String(detail.calories_kcal_dog || 0)} accent={C.amber} />
                <MiniStat label="km/h" value={(detail.avg_speed_kmh || 0).toFixed(1)} accent={C.blue} />
              </div>
              {detail.pace_min_per_km > 0 && (
                <div className="mt-1.5 text-center text-[10px] text-white/55">
                  Andatura media {fmtPace(detail.pace_min_per_km)} min/km
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="rounded-lg py-1.5" style={{ background: `${accent}18`, border: `1px solid ${accent}26` }}>
      <div className="font-display font-bold tabular-nums text-[13px]" style={{ color: accent }}>{value}</div>
      <div className="text-[8.5px] uppercase tracking-widest text-white/50">{label}</div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
   WalkAchievements — dark strip of walk-related badges. Fetches from
   /api/achievements/me and shows walk_total_km + walk_streak + mountain
   + coastal badges. Locked ones stay grey; unlocked shine with the
   badge accent colour and a soft glow.
   ──────────────────────────────────────────────────────────────────── */
function WalkAchievements() {
  const [items, setItems] = useState([]);
  const [unlockedPopup, setUnlockedPopup] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/achievements/me");
        const all = data?.achievements || [];
        // Show only walk-related ones on this page.
        const walk = all.filter((a) =>
          a.id.startsWith("walk_") || a.id === "first_mountain" || a.id === "first_coastal"
        );
        setItems(walk);

        // Detect NEW unlocks since the last time this device opened the
        // page. Any walk badge that's now unlocked but not in our local
        // "seen" set triggers the celebratory overlay.
        try {
          const seenRaw = localStorage.getItem("orio.walk.badges.seen") || "[]";
          const seen = new Set(JSON.parse(seenRaw));
          const fresh = walk.find((a) => a.unlocked && !seen.has(a.id));
          if (fresh) {
            setUnlockedPopup(fresh);
          }
          // Persist ALL currently-unlocked ids so next time only genuinely
          // new unlocks trigger the animation.
          const nowSeen = walk.filter((a) => a.unlocked).map((a) => a.id);
          localStorage.setItem("orio.walk.badges.seen", JSON.stringify(nowSeen));
        } catch { /* silent */ }
      } catch { /* silent */ }
    })();
  }, []);
  if (items.length === 0) return null;
  const unlockedCount = items.filter((a) => a.unlocked).length;
  return (
    <div className="mt-6" data-testid="walk-achievements">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50">Traguardi</div>
        <div className="text-[11px] text-white/60 tabular-nums">
          {unlockedCount}/{items.length} sbloccati
        </div>
      </div>
      <div className="flex gap-2.5 overflow-x-auto orio-h-scroll pb-1">
        {items.map((a) => {
          const pct = Math.min(1, (a.progress || 0) / (a.target || 1));
          const locked = !a.unlocked;
          return (
            <div
              key={a.id}
              data-testid={`walk-badge-${a.id}`}
              className="shrink-0 rounded-2xl border p-3 text-center"
              style={{
                width: 118,
                background: locked ? "rgba(255,255,255,0.04)" : `${a.color}18`,
                borderColor: locked ? "rgba(255,255,255,0.10)" : `${a.color}66`,
                boxShadow: locked ? "none" : `0 12px 28px -14px ${a.color}66`,
                opacity: locked ? 0.72 : 1,
              }}
            >
              <div
                className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                style={{
                  background: locked
                    ? "rgba(255,255,255,0.06)"
                    : `radial-gradient(circle at 30% 30%, ${a.color}44 0%, ${a.color}00 70%), ${a.color}22`,
                  color: locked ? "rgba(255,255,255,0.5)" : a.color,
                }}
              >
                <Trophy className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="mt-2 text-[11.5px] font-semibold text-white leading-tight line-clamp-2">
                {a.label}
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-1 rounded-full overflow-hidden"
                   style={{ background: "rgba(255,255,255,0.08)" }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct * 100}%`,
                    background: locked ? "rgba(255,255,255,0.35)" : a.color,
                  }}
                />
              </div>
              <div className="mt-1 text-[9.5px] text-white/50 tabular-nums">
                {a.progress}/{a.target}
              </div>
            </div>
          );
        })}
      </div>
      <BadgeUnlockAnimation badge={unlockedPopup} onClose={() => setUnlockedPopup(null)} />
    </div>
  );
}
